import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AppSvgIconComponent, SignalButton, State } from '@vallift/ngl';
import { environment } from '@environments/environment';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import { navigateAfterLogin } from '@core/utils/auth-routing.util';
import type {
  AcceptWorkspaceInvitationApiResponse,
  PreviewWorkspaceInvitationApiResponse,
} from '@core/api/workspace/models';

type PageStatus = 'loading' | 'preview' | 'error' | 'email-mismatch';

@Component({
  selector: 'app-accept-invite-page',
  imports: [RouterLink, AppSvgIconComponent, SignalButton],
  templateUrl: './accept-invite-page.html',
  styleUrl: './accept-invite-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcceptInvitePage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  readonly tokenStorage = inject(TokenStorageService);

  readonly previewState = new State<PreviewWorkspaceInvitationApiResponse>();
  readonly acceptState = new State<AcceptWorkspaceInvitationApiResponse>();

  readonly token = signal<string | null>(null);
  readonly pageStatus = signal<PageStatus>('loading');
  readonly errorMessage = signal<string | null>(null);
  readonly currentYear = new Date().getFullYear();

  readonly preview = computed(() => this.previewState.response()?.data ?? null);
  readonly returnUrl = computed(() => {
    const value = this.token();
    return value ? `/accept-invite?token=${encodeURIComponent(value)}` : '/accept-invite';
  });

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');

    if (!token) {
      this.pageStatus.set('error');
      this.errorMessage.set('This invitation link is invalid.');
      return;
    }

    this.token.set(token);
    this.loadPreview(token);
  }

  onGoogleSignInClicked(): void {
    const token = this.token();
    if (!token) return;

    const params = new URLSearchParams({
      purpose: 'ACCEPT_INVITATION',
      token,
    });

    window.location.href = `${environment.baseUrl}/auth/google?${params}`;
  }

  onSignInClicked(): void {
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.returnUrl() },
    });
  }

  onCreateAccountClicked(): void {
    const invitedEmail = this.preview()?.invitedEmail;
    this.router.navigate(['/signup'], {
      queryParams: {
        ...(invitedEmail ? { email: invitedEmail } : {}),
        returnUrl: this.returnUrl(),
      },
    });
  }

  onAcceptClicked(): void {
    const token = this.token();
    if (!token) return;
    this.tryAcceptInvitation(token);
  }

  private loadPreview(token: string): void {
    this.executeRequest({
      state: this.previewState,
      request: this.userAPIService.previewWorkspaceInvitation({ token }),
      handleError: false,
      onSuccess: () => {
        this.pageStatus.set('preview');

        if (this.tokenStorage.isAuthenticated()) {
          this.tryAcceptInvitation(token);
        }
      },
      onFailed: (error) => this.handlePreviewError(error),
    });
  }

  private tryAcceptInvitation(token: string): void {
    const preview = this.preview();
    const user = this.tokenStorage.getUser();

    if (preview && user && user.email.toLowerCase() !== preview.invitedEmail.toLowerCase()) {
      this.pageStatus.set('email-mismatch');
      return;
    }

    this.executeRequest({
      state: this.acceptState,
      request: this.userAPIService.acceptWorkspaceInvitation({ token }),
      showLoader: true,
      handleError: false,
      onSuccess: (response) => {
        this.tokenStorage.setSession(response.data);
        navigateAfterLogin(this.router, {
          workspace: response.data.workspace,
          returnUrl: '/dashboard',
        });
      },
      onFailed: (error) => this.handleAcceptError(error),
    });
  }

  private handlePreviewError(error: HttpErrorResponse): void {
    if (
      this.tokenStorage.isAuthenticated() &&
      this.tokenStorage.hasWorkspaceSession()
    ) {
      void this.router.navigate(['/dashboard']);
      return;
    }

    this.pageStatus.set('error');
    const messages = error.error?.errorMessages ?? [];
    const message = messages[0] ?? 'This invitation link is invalid or has expired.';
    this.errorMessage.set(message);
  }

  private handleAcceptError(error: HttpErrorResponse): void {
    const messages = error.error?.errorMessages ?? [];
    const message = messages[0] ?? 'Could not accept this invitation.';

    if (error.status === 400 && message.toLowerCase().includes('different email')) {
      this.pageStatus.set('email-mismatch');
      return;
    }

    this.toasterService.error(message);
  }
}
